import { inject, Injectable, signal } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of } from 'rxjs';

import { Ability } from '@core/abilities-types';
import { UserSelectors } from '@core/account-data';
import { User } from '@core/account-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import { filterBoolean } from '@core/common-legacy';
import {
  DisqualifyReasonsAutomationsSelectors,
  DisqualifyReasonsSelectors,
} from '@core/disqualify-reasons-data';
import { JobTemplateFull } from '@core/job-templates-types';
import { JobsWrapperDataService, JobWorkflowAutomationsSelectors } from '@core/offers-data';
import { JobItem } from '@core/offers-types';
import {
  AutomationTriggerType,
  TriggerCategory,
  WorkflowAutomation,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';
import {
  JobTemplatesFullDataService,
  JobTemplateWorkflowAutomationsSelectors,
} from '@recruitee/job-templates-data';
import { AutomationsJobItem, JobKind } from '@recruitee/workflow-automations-types';
import { PipelineTemplateStageAutomationsSelectors } from '@recruitee/workflow-templates-data';

import { AutomationActionsDispatcher } from './automation-actions-dispatcher';
import { AutomationOptionsDataService } from './automation-options-data.service';

export enum AutomationWizardDataContext {
  PipelineTemplate = 'pipelineTemplate',
  DisqualifyReason = 'disqualifyReason',
  Job = 'job',
  JobTemplate = 'jobTemplate',
}

@Injectable()
export class AutomationsWizardFacade {
  private readonly store = inject(Store);
  private readonly jobsWrapperDataService = inject(JobsWrapperDataService);
  private readonly jobTemplatesFullDataService = inject(JobTemplatesFullDataService);
  private readonly automationActionsDispatcher = inject(AutomationActionsDispatcher);
  private readonly automationOptionsDataService = inject(AutomationOptionsDataService);

  public dataContext = signal<AutomationWizardDataContext | null>(null);

  private allUsersMap = this.store.selectSignal(UserSelectors.entityMap);

  public getAutomationData(id: number): Observable<WorkflowAutomation | null> {
    if (this.dataContext() === AutomationWizardDataContext.PipelineTemplate) {
      return this.store.select(PipelineTemplateStageAutomationsSelectors.byId(id));
    }
    if (this.dataContext() === AutomationWizardDataContext.DisqualifyReason) {
      return this.store.select(DisqualifyReasonsAutomationsSelectors.byId(id));
    }
    if (this.dataContext() === AutomationWizardDataContext.Job) {
      return this.store.select(JobWorkflowAutomationsSelectors.byId(id));
    }
    if (this.dataContext() === AutomationWizardDataContext.JobTemplate) {
      return this.store.select(JobTemplateWorkflowAutomationsSelectors.byId(id));
    }
    return of(null);
  }

  public getAutomationOptionsData(data: {
    pipelineTemplateId?: number;
    disqualifyReasonId?: number;
    jobId?: number;
    jobTemplateId?: number;
  }): Observable<WorkflowAutomationOptionsData> {
    return this.automationOptionsDataService.fetchOptions(data);
  }

  public getUsersData(
    jobId?: number,
  ): Observable<Record<TriggerCategory, { allowedUsers: User[]; disallowedUsers: User[] }>> {
    return combineLatest([
      this.store.select(UserSelectors.entities()),
      jobId
        ? this.store.select(FullCandidateSelectors.offerUsersGrouped({ offerId: jobId }))
        : of(null),
    ]).pipe(
      map(([allUsers, usersGrouped]) => {
        const jobUsers = allUsers.reduce(
          (acc, user) => {
            if (user.roleAbilities.includes(Ability.ViewJobs)) {
              acc.allowedUsers.push(user);
            } else {
              acc.disallowedUsers.push(user);
            }
            return acc;
          },
          <{ allowedUsers: User[]; disallowedUsers: User[] }>{
            allowedUsers: [],
            disallowedUsers: [],
          },
        );
        const candidateUsers = usersGrouped
          ? {
              allowedUsers: usersGrouped.usersWithAccess,
              disallowedUsers: usersGrouped.usersWithoutAccess,
            }
          : { allowedUsers: allUsers, disallowedUsers: [] };

        return {
          [TriggerCategory.Job]: jobUsers,
          [TriggerCategory.Candidate]: candidateUsers,
        };
      }),
    );
  }

  public getJobData(jobId?: number, jobTemplateId?: number): Observable<AutomationsJobItem | null> {
    let source$: Observable<JobItem | JobTemplateFull | null>;
    if (jobId) {
      source$ = this.jobsWrapperDataService.getJobItemById$(jobId);
    } else {
      source$ = this.jobTemplatesFullDataService.getFullOneById(jobTemplateId!);
    }

    return source$.pipe(
      filterBoolean,
      map(job => {
        const recruiter = job.recruiterId ? this.allUsersMap()?.[job.recruiterId] : null;
        const hiringManager = job.hiringManagerId
          ? this.allUsersMap()?.[job.hiringManagerId]
          : null;
        return {
          id: job.id,
          recruiter,
          hiringManager,
          kind: jobId ? JobKind.Job : JobKind.JobTemplate,
        };
      }),
    );
  }

  public setAutomationDataContext({
    pipelineTemplateId,
    disqualifyReasonId,
    jobId,
    jobTemplateId,
  }: {
    pipelineTemplateId?: number;
    disqualifyReasonId?: number;
    jobId?: number;
    jobTemplateId?: number;
  }): void {
    if (pipelineTemplateId) {
      this.dataContext.set(AutomationWizardDataContext.PipelineTemplate);
    } else if (disqualifyReasonId) {
      this.dataContext.set(AutomationWizardDataContext.DisqualifyReason);
    } else if (jobId) {
      this.dataContext.set(AutomationWizardDataContext.Job);
    } else if (jobTemplateId) {
      this.dataContext.set(AutomationWizardDataContext.JobTemplate);
    } else {
      this.dataContext.set(null);
    }
  }

  public createAutomation(
    automation: WorkflowAutomationCreatePayload,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<boolean> {
    return this.automationActionsDispatcher.createAutomation(automation, additionalData);
  }

  public updateAutomation(
    automation: WorkflowAutomationUpdatePayload,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<boolean> {
    return this.automationActionsDispatcher.updateAutomation(automation, additionalData);
  }

  public deleteAutomation(
    automationId: number,
    triggerType: AutomationTriggerType,
    additionalData: WorkflowAutomationAdditionalData,
  ): Observable<boolean> {
    return this.automationActionsDispatcher.deleteAutomation(
      automationId,
      triggerType,
      additionalData,
    );
  }

  public getDisqualifyReason(id: number) {
    return this.store.select(DisqualifyReasonsSelectors.byId(id)).pipe(filterBoolean);
  }
}
